import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import LanguageToggle from './LanguageToggle';

export default function SetPassword({ isInitial }) {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('setpassword.err_too_short'));
      return;
    }
    if (password !== confirm) {
      setError(t('setpassword.err_no_match'));
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      if (user) {
        try {
          const { error: updErr, data: updData } = await supabase
            .from('user_profiles')
            .update({
              password_set: true,
              user_id: user.id,
              updated_at: new Date().toISOString()
            })
            .eq('email', user.email.toLowerCase())
            .select();
          if (updErr) console.warn('Profile flag update error:', updErr);
          if (!updData || updData.length === 0) {
            console.warn('Profile update matched 0 rows for', user.email);
          }
          if (refreshProfile) await refreshProfile();
        } catch (e) {
          console.warn('Profile flag update failed:', e);
        }
      }

      setSuccess(true);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      setError(t('setpassword.err_generic'));
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: 20,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        padding: '36px 32px',
        boxShadow: 'var(--shadow2)',
      }}>
        {/* Language toggle — top right */}
        {isInitial && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <LanguageToggle />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <div style={{
            width: 36, height: 36, background: 'var(--blue)',
            borderRadius: 9, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'white',
            letterSpacing: '-.03em',
          }}>PP</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.04em', color: 'var(--t0)' }}>
              MAP <span style={{ color: 'var(--blue)' }}>v5</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--t2)', marginTop: 1 }}>
              {t('setpassword.tagline_welcome')}
            </div>
          </div>
        </div>

        {success ? (
          <>
            <div style={{
              width: 50, height: 50, borderRadius: '50%',
              background: 'var(--green-l)', border: '1px solid var(--green-b)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, marginBottom: 16, color: 'var(--green)',
            }}>✓</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--t0)', marginBottom: 6, letterSpacing: '-.02em' }}>
              {t('setpassword.success_title')}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.6 }}>
              {t('setpassword.success_body')}
            </p>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--t0)', marginBottom: 6, letterSpacing: '-.02em' }}>
              {isInitial ? t('setpassword.title_initial') : t('setpassword.title_change')}
            </h2>
            <p style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 20, lineHeight: 1.6 }}>
              {isInitial
                ? t('setpassword.subtitle_initial', { email: user?.email })
                : t('setpassword.subtitle_change')}
              {' '}{t('setpassword.subtitle_min_chars')}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <div className="form-label">
                  <span className="form-label-text">{t('setpassword.label_new')}</span>
                </div>
                <input
                  type="password"
                  className="form-input"
                  placeholder={t('setpassword.placeholder_new')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                  minLength={8}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <div className="form-label">
                  <span className="form-label-text">{t('setpassword.label_confirm')}</span>
                </div>
                <input
                  type="password"
                  className="form-input"
                  placeholder={t('setpassword.placeholder_confirm')}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={loading}
                />
              </div>

              {error && (
                <div style={{
                  background: 'var(--red-l)',
                  border: '1px solid var(--red-b)',
                  borderRadius: 'var(--radius)',
                  padding: '9px 13px',
                  fontSize: 12,
                  color: '#fca5a5',
                  marginBottom: 14,
                  lineHeight: 1.5,
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary btn-lg"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                {loading ? t('setpassword.btn_loading') : isInitial ? t('setpassword.btn_initial') : t('setpassword.btn_change')}
              </button>
            </form>

            <div style={{
              marginTop: 22,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
              fontSize: 11,
              color: 'var(--t3)',
              lineHeight: 1.6,
              textAlign: 'center',
            }}>
              {t('setpassword.footer_secure')}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
